import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

const Williston = () => {
  useEffect(() => {
    // Set document title and meta tags
    document.title = 'We Buy Valves Williston | Industrial Equipment Buyers Williston North Dakota';
    
    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Leading valve buyers in Williston serving Bakken oil field operations. We buy surplus valves, control systems, and instrumentation from Williston production facilities at above scrap rates.');
    }
    
    // Set meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'we buy valves williston, williston valve buyers, bakken valve buyers, north dakota industrial equipment buyers, williston surplus valves');
    
    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://valvesalessupply.com/williston');
    
    // Set Open Graph tags
    const ogTags = [
      { property: 'og:title', content: 'We Buy Valves Williston | Industrial Equipment Buyers Williston North Dakota' },
      { property: 'og:description', content: 'Leading valve buyers in Williston serving Bakken oil field operations and processing facilities.' },
      { property: 'og:url', content: 'https://valvesalessupply.com/williston' },
      { property: 'og:type', content: 'website' }
    ];
    
    ogTags.forEach(tag => {
      let ogTag = document.querySelector(`meta[property="${tag.property}"]`);
      if (!ogTag) {
        ogTag = document.createElement('meta');
        ogTag.setAttribute('property', tag.property);
        document.head.appendChild(ogTag);
      }
      ogTag.setAttribute('content', tag.content);
    });
    
    // Set geo location tags
    let geoPosition = document.querySelector('meta[name="geo.position"]');
    if (!geoPosition) {
      geoPosition = document.createElement('meta');
      geoPosition.setAttribute('name', 'geo.position');
      document.head.appendChild(geoPosition);
    }
    geoPosition.setAttribute('content', '48.1470;-103.6180');
    
    let geoRegion = document.querySelector('meta[name="geo.region"]');
    if (!geoRegion) {
      geoRegion = document.createElement('meta');
      geoRegion.setAttribute('name', 'geo.region');
      document.head.appendChild(geoRegion);
    }
    geoRegion.setAttribute('content', 'US-ND');
    
    let geoPlacename = document.querySelector('meta[name="geo.placename"]');
    if (!geoPlacename) {
      geoPlacename = document.createElement('meta');
      geoPlacename.setAttribute('name', 'geo.placename');
      document.head.appendChild(geoPlacename);
    }
    geoPlacename.setAttribute('content', 'Williston, North Dakota');

    // Add Schema.org structured data
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "LocalBusiness",
          "name": "ValveSalesSupply Williston",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Williston",
            "addressRegion": "ND",
            "addressCountry": "US"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": 48.1470,
            "longitude": -103.6180
          },
          "telephone": "+1-214-854-5638",
          "description": "Leading valve buyers in Williston serving Bakken oil field operations. We purchase surplus valves, control systems, and instrumentation from production facilities."
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://valvesalessupply.com"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "North Dakota",
              "item": "https://valvesalessupply.com/north-dakota"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Williston",
              "item": "https://valvesalessupply.com/williston"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Do you buy valves in Williston?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, we buy surplus valves in Williston including control valves, wellhead equipment, and instrumentation from Bakken oil field operations at above scrap rates with 8-12 hour response times."
              }
            },
            {
              "@type": "Question",
              "name": "What equipment do you purchase in Williston?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We purchase Fisher control valves, Masoneilan valves, wellhead equipment, separators, pumps, compressors, Rosemount transmitters, and complete instrumentation systems from Williston oil and gas facilities."
              }
            }
          ]
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const nearbyFacilities = [
    { name: 'Bakken Oil Fields', type: 'Oil Production', capacity: '5,000+ Wells' },
    { name: 'Processing Plants', type: 'Gas Processing', capacity: 'Major Facilities' },
    { name: 'Pipeline Operations', type: 'Midstream', capacity: 'Regional Hub' }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/7138e65916e666b07e2f2ebd84e60610.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#1B3A5F]/90 to-[#1B3A5F]/70"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-white">
          <div className="max-w-4xl">
            <h1 className="text-5xl font-bold mb-6">We Buy Valves Williston</h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Leading <strong>valve buyers in Williston</strong> serving the Bakken oil field operations. 
              Valve Sales Supply purchases surplus valves, control systems, and instrumentation from Williston 
              production facilities and processing plants at above scrap rates.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="tel:214-854-5638"
                className="bg-white text-[#1B3A5F] px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all duration-300 inline-flex items-center justify-center whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                CALL 214-854-5638
              </a>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-[#1B3A5F] transition-all duration-300 inline-flex items-center justify-center whitespace-nowrap"
              >
                GET WILLISTON QUOTE
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Market Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-[#1B3A5F] text-4xl font-bold mb-6">WILLISTON BAKKEN EQUIPMENT BUYER</h2>
              <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                Williston is the heart of North Dakota's Bakken oil boom with extensive production operations. 
                As experienced <strong className="text-[#1B3A5F]">surplus valve buyers in Williston</strong>, 
                we provide professional equipment purchasing services for oil field and processing facilities.
              </p>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                We specialize in purchasing <strong className="text-[#1B3A5F]">Fisher control valves</strong>, 
                Rosemount transmitters, and complete instrumentation systems from Williston oil and gas operations.
              </p>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-[#1B3A5F] text-3xl font-bold">5,000+</div>
                  <div className="text-gray-600">Production Wells</div>
                </div>
                <div className="text-center">
                  <div className="text-[#1B3A5F] text-3xl font-bold">8-12 Hrs</div>
                  <div className="text-gray-600">Response Time</div>
                </div>
                <div className="text-center">
                  <div className="text-[#1B3A5F] text-3xl font-bold">15 Years</div>
                  <div className="text-gray-600">Bakken Experience</div>
                </div>
              </div>
            </div>
            
            <div className="bg-[#F5F7FA] rounded-3xl p-8">
              <h3 className="text-[#1B3A5F] text-2xl font-bold mb-6">WILLISTON FACILITIES</h3>
              <div className="space-y-4">
                {nearbyFacilities.map((facility, index) => (
                  <div key={index} className="bg-white rounded-lg p-4">
                    <h4 className="text-[#1B3A5F] font-bold text-lg">{facility.name}</h4>
                    <div className="flex justify-between mt-2">
                      <span className="text-gray-600 text-sm">{facility.type}</span>
                      <span className="text-[#1B3A5F] font-semibold text-sm">{facility.capacity}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Sell Valves to Us */}
      <section className="py-20 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-[#1B3A5F] text-4xl font-bold mb-4">WHY SELL VALVES TO US IN WILLISTON</h2>
            <p className="text-gray-600 text-xl">Bakken oil field expertise with professional service</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Above Scrap Rates',
                description: 'Get maximum value for your surplus valves and equipment',
                icon: 'ri-money-dollar-circle-line'
              },
              {
                title: 'Fast Response',
                description: '8-12 hour response times throughout Williston area',
                icon: 'ri-time-line'
              },
              {
                title: 'Bakken Expertise',
                description: '15+ years experience in North Dakota oil field operations',
                icon: 'ri-oil-line'
              },
              {
                title: 'Professional Service',
                description: 'Complete compliance documentation and coordination',
                icon: 'ri-file-list-3-line'
              },
              {
                title: 'Equipment Knowledge',
                description: 'Expert assessment of Fisher, Masoneilan, and Rosemount equipment',
                icon: 'ri-tools-line'
              },
              {
                title: 'Quick Payment',
                description: 'Fast payment upon equipment pickup',
                icon: 'ri-bank-card-line'
              }
            ].map((benefit, index) => (
              <div key={index} className="bg-white rounded-3xl p-8 text-center hover:shadow-lg transition-all duration-300">
                <div className="w-16 h-16 bg-[#1B3A5F] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${benefit.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-[#1B3A5F] text-xl font-bold mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment Categories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-[#1B3A5F] text-4xl font-bold mb-4">SELL VALVES WILLISTON ND</h2>
            <p className="text-gray-600 text-xl">Professional equipment purchasing from Williston Bakken operations</p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Control Valves',
                equipment: ['Fisher Control Valves', 'Masoneilan Valves', 'Ball Valves', 'Gate Valves', 'Check Valves'],
                description: 'Premium control valves from Williston oil fields'
              },
              {
                title: 'Production Equipment',
                equipment: ['Wellhead Equipment', 'Separators', 'Pumps', 'Compressors'],
                description: 'Oil & gas production equipment from Bakken operations'
              },
              {
                title: 'Instrumentation',
                equipment: ['Rosemount Transmitters', 'Flow Meters', 'Pressure Instruments', 'Safety Systems'],
                description: 'Process instrumentation from North Dakota facilities'
              }
            ].map((category, index) => (
              <div key={index} className="bg-[#F5F7FA] rounded-3xl p-8 hover:shadow-lg transition-all duration-300">
                <h3 className="text-[#1B3A5F] text-2xl font-bold mb-4">{category.title}</h3>
                <p className="text-gray-600 mb-6">{category.description}</p>
                
                <div className="space-y-3">
                  {category.equipment.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-center">
                      <i className="ri-check-line text-[#1B3A5F] mr-3"></i>
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Markets */}
      <section className="py-20 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-[#1B3A5F] text-4xl font-bold mb-4">RELATED NORTH DAKOTA MARKETS</h2>
            <p className="text-gray-600 text-xl">We serve all major North Dakota oil field operations</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { city: 'Bismarck', link: '/bismarck' },
              { city: 'Minot', link: '/minot' },
              { city: 'Dickinson', link: '/dickinson' }
            ].map((market, index) => (
              <Link
                key={index}
                to={market.link}
                className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300 border-2 border-transparent hover:border-[#1B3A5F]"
              >
                <h3 className="text-[#1B3A5F] text-xl font-bold mb-2">{market.city}</h3>
                <div className="text-gray-600 text-sm mb-3">ND Valve Buyers</div>
                <div className="text-[#1B3A5F] font-semibold inline-flex items-center">
                  View Details
                  <i className="ri-arrow-right-line ml-2"></i>
                </div>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <Link
              to="/north-dakota"
              className="text-[#1B3A5F] font-semibold hover:underline inline-flex items-center text-lg"
            >
              View All North Dakota Cities
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-[#1B3A5F]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h2 className="text-4xl font-bold mb-6">WILLISTON EQUIPMENT BUYER</h2>
              <p className="text-xl text-gray-200 mb-8">
                Contact Mark Driver for professional equipment purchasing services in Williston
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <i className="ri-phone-line text-2xl mr-4"></i>
                  <div>
                    <div className="font-bold text-xl">214-854-5638</div>
                    <div className="text-gray-300">Direct Line - Mark Driver</div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <i className="ri-mail-line text-2xl mr-4"></i>
                  <div>
                    <div className="font-bold">williston@valvesalessupply.com</div>
                    <div className="text-gray-300">Equipment Lists & Photos</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-3xl p-8">
              <h3 className="text-[#1B3A5F] text-2xl font-bold mb-6">WHY CHOOSE VALVE SALES SUPPLY</h3>
              <div className="space-y-4">
                {[
                  'Above scrap rates for functional equipment',
                  'Fast 8-12 hour response in Williston',
                  'Expert knowledge of Bakken operations',
                  'Professional turnaround coordination',
                  'Complete compliance documentation',
                  '15+ years North Dakota market experience'
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center">
                    <i className="ri-check-line text-[#1B3A5F] text-xl mr-3"></i>
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8">
                <Link
                  to="/contact"
                  className="bg-[#1B3A5F] text-white px-8 py-4 rounded-full font-bold hover:bg-[#1B3A5F]/90 transition-all duration-300 inline-flex items-center justify-center w-full whitespace-nowrap"
                >
                  GET QUOTE TODAY
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Williston;
